import React, { useState } from "react";
import {
  FaEnvelopeOpenText,
  FaBookOpen,
  FaGraduationCap,
  FaCommentDots,
} from "react-icons/fa";
import PcInactivityOverviewBlock from "./PcInactivityOverviewBlock";
import NeverUsedOverviewBlock from "./NeverUsedOverviewBlock";

/**
 * "Never used" directory tabs (non-PC signals). Each entry drives one
 * NeverUsedOverviewBlock, which now carries the same tooling as the PC
 * inactivity tab: signup-age filters, sortable columns, selection, and a bulk
 * outreach email. `sendLabel` names each signal's own email, because every
 * signal keeps a separate template — telling an org that never uploaded
 * guidelines about its last checklist run would be false.
 */
const NEVER_TABS = [
  {
    id: "never_guidelines",
    label: "Never guidelines",
    icon: <FaBookOpen />,
    title: "Never uploaded investor guidelines",
    hint: "Active orgs that have never applied any investor guidelines to a loan program.",
    emptyLabel: "All active organizations have uploaded investor guidelines.",
    sendLabel: "Send guidelines outreach email",
  },
  {
    id: "never_trained",
    label: "Never trained",
    icon: <FaGraduationCap />,
    title: "Never trained the agent",
    hint: 'Active orgs with no processing checklist "Train your agent" custom instructions.',
    emptyLabel: "All active organizations have added training instructions.",
    sendLabel: "Send training outreach email",
  },
  {
    id: "never_ask_addy",
    label: "Never Ask Addy",
    icon: <FaCommentDots />,
    title: "Never used Ask Addy",
    hint: "Active orgs whose members have never started a sidebar Ask Addy chat.",
    emptyLabel: "All active organizations have used Ask Addy.",
    sendLabel: "Send Ask Addy outreach email",
  },
];

const PC_TAB = {
  id: "pc",
  label: "PC inactivity",
  icon: <FaEnvelopeOpenText />,
};

const TABS = [PC_TAB, ...NEVER_TABS];

/**
 * Tabbed adoption directory for the Org 360 landing page. Keeps the existing PC
 * inactivity overview as the first tab and adds "never used" directories for
 * investor guidelines, checklist training, and Ask Addy.
 *
 * @param {{ env: string, onSelect: (org: { id: string, name: string }) => void }} props
 */
export default function AdoptionOverviewTabs({ env, onSelect }) {
  const [activeTab, setActiveTab] = useState(PC_TAB.id);
  const activeNeverTab = NEVER_TABS.find((tab) => tab.id === activeTab);

  return (
    <div className="oo-adoption-tabs">
      <div className="oo-adoption-tabbar" role="tablist">
        <div className="oo-seg">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`oo-seg-btn ${
                activeTab === tab.id ? "oo-seg-btn--on" : ""
              }`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === PC_TAB.id ? (
        <PcInactivityOverviewBlock env={env} onSelect={onSelect} />
      ) : (
        <div className="oo-landing-block">
          <div className="oo-landing-block-head">
            {activeNeverTab.icon} {activeNeverTab.title}
          </div>
          <NeverUsedOverviewBlock
            env={env}
            signal={activeNeverTab.id}
            hint={activeNeverTab.hint}
            emptyLabel={activeNeverTab.emptyLabel}
            sendLabel={activeNeverTab.sendLabel}
            onSelect={onSelect}
          />
        </div>
      )}
    </div>
  );
}
